import { useState, useEffect, ReactElement, useCallback } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
} from '@tanstack/react-table'
import { useParams } from 'react-router-dom'
import styles from './BedTable.module.scss'
import CreateBed from './CreateBed'
import EditBed from './EditBed'
import Button from '../../../../../components/ButtonD/index'
import CheckBox from '../../../../../components/CheckBox'
import ErrorPopUp from '../../../../../components/ErrorPopup/ErrorPopUp'
import InitialTableComponent from '../../../../../components/InitialTableComponent/InitialTableComponent'
import Loading from '../../../../../components/Loading/Loading'
import { usePagination, DOTS } from '../../../../../components/Pagination/usePagination'
import wave from '../../../../../images/wave.svg'
import { setSideBarActive } from '../../../../../redux/operator/clinics'
import { getBeds } from '../../../../../redux/operator/clinics/Bed'
import { RootState, useAppDispatch, useAppSelector } from '../../../../../redux/store'
import { IBed, StatusEnum } from '../../../../../redux/type'

const BedTable = () => {
  const { id } = useParams()
  const clinicId: any = id
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })

  const [currentPage, setCurrentPage] = useState(1)
  const siblingCount = 1
  const pagination = {
    pageIndex,
    pageSize,
  }
  const [loadingComponent, setLoadingComponent] = useState(true)
  const [sideBarOnInitialComponent, setsideBarOnInitialComponent] = useState(false)
  const dispatch = useAppDispatch()
  const beds: any = useAppSelector((state: RootState) => state.bed.beds)
  const metaData = useAppSelector((state: RootState) => state.bed.bedsMetaData)
  const error: any = useAppSelector((state: RootState) => state.bed.error)
  const status: any = useAppSelector((state: RootState) => state.bed.getBedsStatus)
  const totalCount = metaData?.totalRecordCount ?? 0
  const [sideBar, setSideBar] = useState(false)
  const [count, setCount] = useState(1)
  const [selectAll, setSelectAll] = useState(false)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [displayNone, setDisplayNone] = useState('none')
  const [bedId, setBedId] = useState<string>('')

  const handleFormSubmit = async () => {
    // setLoadingComponent(true)
    // dispatch(setIsLoading(true))
    await dispatch(getBeds({ currentPage, pageSize, clinicId }))
    setCount((prevCount) => prevCount + 1)
    setSideBar(false)
  }

  const handleRowClick = (row: any) => {
    dispatch(setSideBarActive(true))
    setSideBar(true)
    setDisplayNone('block')
    setBedId(row.original.id)
  }

  const parentLoading = () => {
    setLoadingComponent(true)
  }

  const handleHeaderCheckboxChange = () => {
    setSelectAll(!selectAll)
    const allRowIds = Table.getRowModel().rows.map((row) => row.id)
    setSelectedRows(selectAll ? [] : allRowIds)
  }

  const handleCheckboxChange = (rowId: string) => {
    const updatedSelectedRows = selectedRows.includes(rowId)
      ? selectedRows.filter((id) => id !== rowId)
      : [...selectedRows, rowId]
    setSelectedRows(updatedSelectedRows)
    setSelectAll(updatedSelectedRows.length === Table.getRowModel().rows.length)
  }

  const columns: ColumnDef<IBed, ReactElement>[] = [
    {
      accessorKey: 'checkbox',
      header: () => (
        <div style={{ paddingTop: '1rem' }}>
          <CheckBox isSelected={selectAll} onChange={() => handleHeaderCheckboxChange()} />
        </div>
      ),
      cell: (props) => (
        <CheckBox
          isSelected={selectedRows.includes(props.row.id)}
          onChange={() => handleCheckboxChange(props.row.id)}
        />
      ),
      size: 30,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (props) => <p>{props.getValue()}</p>,
    },
  ]

  const Table = useReactTable({
    data: beds,
    columns: columns,
    state: {
      pagination,
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })
  const paginationRange: any = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  })

  const lastPage = paginationRange[paginationRange?.length - 1]

  const fetchData = useCallback(() => {
    dispatch(getBeds({ currentPage, pageSize, clinicId })).finally(() => setLoadingComponent(false))
  }, [currentPage])

  useEffect(() => {
    fetchData()
  }, [fetchData, count])

  if (loadingComponent === true) {
    return (
      <div className={styles.loadingGlobal}>
        <Loading style={{ width: '78vw', height: '79vh', background: 'none' }}></Loading>
      </div>
    )
  }

  if (error >= 400 && error <= 599) {
    return (
      <div className={styles.global}>
        <ErrorPopUp
          ButtonContent={'Retry'}
          onClick={() => {
            window.location.reload()
          }}
        ></ErrorPopUp>
      </div>
    )
  }

  if (metaData !== undefined && totalCount === 0) {
    return (
      <div>
        <div className={styles.global}>
          <InitialTableComponent
            onClick={() => {
              setsideBarOnInitialComponent(true)
              dispatch(setSideBarActive(true))
            }}
            ButtonContent={'Create'}
            TextContent={'Bed'}
            styles={sideBarOnInitialComponent ? { width: 'calc(80vw - 35rem)' } : {}}
            svg={wave}
          ></InitialTableComponent>

          <div>
            <CreateBed
              style={sideBarOnInitialComponent ? { right: '0' } : {}}
              onClose={() => {
                setsideBarOnInitialComponent(false)
                dispatch(setSideBarActive(false))
              }}
              // onClick={() => {
              //   setsideBarOnInitialComponent(true)
              // }}
              handleFormSubmit={handleFormSubmit}
              parentLoading={parentLoading}
            ></CreateBed>
          </div>
        </div>
      </div>
    )
  }

  if (totalCount !== 0) {
    return (
      <div className={styles.global}>
        <div>
          <CreateBed
            style={sideBar ? { right: '0' } : {}}
            onClose={() => {
              setSideBar(false)
              dispatch(setSideBarActive(false))
            }}
            handleFormSubmit={handleFormSubmit}
            parentLoading={parentLoading}
          ></CreateBed>
        </div>
        <div>
          <EditBed
            style={sideBar ? { right: '0', display: `${displayNone}` } : {}}
            onClose={() => {
              setSideBar(false)
              dispatch(setSideBarActive(false))
              setDisplayNone('none')
            }}
            handleFormSubmit={handleFormSubmit}
            parentLoading={parentLoading}
            bedId={bedId}
          ></EditBed>
        </div>

        <div className={sideBar ? styles.sideBar : styles.tableDiv}>
          <div className={styles.overallContainer}>
            <div className={styles.buttonContainer}>
              <div className={styles.tableButton}>
                <Button
                  style={{ width: '12.1rem', color: '#FFFEFF' }}
                  onClick={() => {
                    dispatch(setSideBarActive(true))
                    setSideBar(true)
                  }}
                >
                  Add Bed
                </Button>
              </div>
            </div>
            {status === StatusEnum.Pending ? (
              <div className={styles.loadingStatus}>
                <div>
                  <Loading style={sideBar ? { width: 'calc(78vw - 35rem)' } : {}}></Loading>
                </div>
              </div>
            ) : (
              <div
                className={`${styles.entireDiv}`}
                // onScroll={handleScroll}
                // onMouseLeave={handleMouseLeave}
                // onMouseDown={handleMouseClick}
                // onMouseMove={handleMouseMove}
              >
                <div className={styles.tableContent}>
                  <table className={styles.entireTable}>
                    <thead className={styles.tableHeader}>
                      {Table.getHeaderGroups().map((headerGroup) => (
                        <tr className={styles.theader} key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className={styles.thcell}
                              style={{
                                width: header.getSize() !== 150 ? header.getSize() : undefined,
                              }}
                            >
                              {header.isPlaceholder ? null : (
                                <>{flexRender(header.column.columnDef.header, header.getContext())}</>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody className={styles.tableBody}>
                      {Table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className={styles.tableRow}>
                          {row.getVisibleCells().map((cell, index) => (
                            <td
                              key={cell.id}
                              onClick={
                                index === 0
                                  ? undefined
                                  : () => {
                                      handleRowClick(row)
                                    }
                              }
                              className={styles.tableRowCell}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className={styles.paginationPosition}>
              <div>
                {
                  <ul className={styles.paginationContainer}>
                    <li
                      className={styles.paginationItem}
                      onClick={() => {
                        if (status === StatusEnum.Success) {
                          if (currentPage > 1) {
                            setPagination({
                              pageIndex: pageIndex - 1,
                              pageSize: pageSize,
                            })
                            setCurrentPage(currentPage - 1)
                          }
                        }
                      }}
                    >
                      <div className={`${styles.arrow} ${styles.left}`} />
                    </li>
                    {paginationRange.map((pageNumber: any, i: number) => {
                      if (pageNumber === DOTS) {
                        return (
                          <li key={i} className={`${styles.paginationItem} ${styles.dots}`}>
                            &#8230;
                          </li>
                        )
                      }

                      return (
                        <li
                          key={i}
                          className={`${styles.paginationItem} ${
                            pageNumber === currentPage ? styles.currentPageItem : ''
                          }`}
                          onClick={() => {
                            if (status === StatusEnum.Success) {
                              if (pageNumber !== currentPage) {
                                setCurrentPage(pageNumber)
                                setPagination({
                                  pageIndex: pageNumber - 1,
                                  pageSize: pageSize,
                                })
                                Table.nextPage()
                              }
                            }
                          }}
                        >
                          {pageNumber}
                        </li>
                      )
                    })}
                    <li
                      className={styles.paginationItem}
                      onClick={() => {
                        if (status === StatusEnum.Success) {
                          if (currentPage < lastPage) {
                            setPagination({
                              pageIndex: pageIndex + 1,
                              pageSize: pageSize,
                            })
                            setCurrentPage(currentPage + 1)
                          }
                        }
                      }}
                    >
                      <div className={`${styles.arrow} ${styles.right}`} />
                    </li>
                  </ul>
                }
              </div>
            </div>
          </div>
          {/* {PostOperatorStatus === StatusEnum.Success && showToast && (
            <Toast showToast={showToast} message="Success" type="success"></Toast>
          )} */}
        </div>
      </div>
    )
  }

  return null
}

export default BedTable
